import { FC, useMemo, useState } from 'react';

import { orderBy } from 'lodash';

import { useSeriesPostersReviewsQuery } from '@queries/TagQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { AddPosterPopup } from '@screens/series/SeriesScreen/AddPosterPopup';
import { PosterGrid } from '@screens/series/SeriesScreen/SeriesPostersReviews';

import { SharedDashboardSectionProps } from '../DashboardScreen.types';

export const DashboardPosterGrid: FC<SharedDashboardSectionProps> = ({
  isAppDashboardReady,
  appDashboard,
}) => {
  const { track } = useAnalytics();
  const [isAddFlyerPopupVisible, setIsAddFlyerPopupVisible] = useState(false);
  const { tagId, tagSlug } = appDashboard?.featuredPosters ?? {};
  const { areSeriesPostersReviewsFetched, seriesPosters = [] } =
    useSeriesPostersReviewsQuery(tagSlug);

  const top3SeriesPosters = useMemo(
    () => orderBy(seriesPosters, ({ reviewsCount = 0 }) => reviewsCount, 'desc').slice(0, 3),
    [seriesPosters],
  );

  if (!isAppDashboardReady) {
    return (
      <>
        <div>
          <div className="mb-2">
            <Skeleton height={22} />
          </div>
          <div className="mb-3">
            <Skeleton height={160} />
          </div>
        </div>
      </>
    );
  }

  if (!appDashboard?.featuredPosters) {
    return <></>;
  }

  const backUrl = `/community`;

  return (
    <>
      {top3SeriesPosters.length === 3 && (
        <>
          <TitleToolbar
            text="Top recommended shows"
            size="sm"
            infoButton={{
              title: 'Top recommended shows',
              text: `These shows have the most hype so they're the top recommended upcoming shows!`,
            }}
          />
          <PosterGrid posters={top3SeriesPosters} backUrl={backUrl} />
        </>
      )}

      <TitleToolbar
        text="Upcoming shows"
        size="sm"
        action={
          <Button
            roundness="rounded"
            size="sm"
            startIcon={<FontAwesomeIcon name="plus" />}
            style={{ padding: '4px 10px', borderRadius: 4 }}
            onClick={() => {
              track('Click Add Flyer');
              setIsAddFlyerPopupVisible(true);
            }}
          >
            Add Flyer
          </Button>
        }
      />
      <PosterGrid
        posters={seriesPosters}
        backUrl={backUrl}
        emptyStateProps={{
          isLoading: !areSeriesPostersReviewsFetched,
          title: 'No upcoming shows yet',
          text: `No one has added a flyer for an upcoming show yet. Be the first to add one!`,
          button: {
            text: 'Add Flyer',
            onClick: () => {
              track('Click Add Flyer Empty State');
              setIsAddFlyerPopupVisible(true);
            },
          },
        }}
      />

      <AddPosterPopup
        isVisible={isAddFlyerPopupVisible}
        onClose={() => setIsAddFlyerPopupVisible(false)}
        series={{ _id: tagId }}
        tagSlug={tagSlug}
      />
    </>
  );
};
